/**
 * PlannerDataSection - Displays read-only planner estate planning data within settlement frames.
 *
 * Purpose:
 * Pre-populate settlement forms with data the deceased created during planning.
 * Prevents duplicate data entry and provides context for executors.
 *
 * Usage:
 * <PlannerDataSection
 *   plannerFrameId="frame-146"  // The planner frame to fetch data from
 *   estateId={estateId}
 *   title="Contacts from Estate Plan"
 *   emptyMessage="No contacts found in estate plan"
 *   renderData={(data) => <ContactsList contacts={data.contacts} />}
 * />
 */

import React, { useEffect, useState } from 'react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { ChevronDown, ChevronUp, Eye } from 'lucide-react';

export interface PlannerDataSectionProps {
  /** The planner frame ID to fetch data from (e.g., 'frame-146') */
  plannerFrameId: string;
  /** Estate ID for the settlement */
  estateId: string;
  /** Section title */
  title: string;
  /** Message to show when no planner data exists */
  emptyMessage?: string;
  /** Custom render function for the planner data */
  renderData: (data: Record<string, unknown>) => React.ReactNode;
  /** Optional: Initially expanded state */
  initiallyExpanded?: boolean;
  /** Optional: Additional CSS classes */
  className?: string;
}

export function PlannerDataSection({
  plannerFrameId,
  estateId,
  title,
  emptyMessage = 'No data found in estate plan',
  renderData,
  initiallyExpanded = false,
  className = '',
}: PlannerDataSectionProps) {
  const { loadPlannerData, isLoading, error } = useSettlementSession(estateId);
  const [plannerData, setPlannerData] = useState<Record<string, unknown> | null>(null);
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  useEffect(() => {
    const fetchPlannerData = async () => {
      const data = await loadPlannerData(plannerFrameId);
      setPlannerData(data);
    };

    fetchPlannerData();
  }, [plannerFrameId, loadPlannerData]);

  // Don't render if no data and not loading
  if (!isLoading && !plannerData) {
    return null;
  }

  const hasData = plannerData && Object.keys(plannerData).length > 0;

  return (
    <div className={`bg-cyan-50 border border-cyan-200 rounded-lg p-4 ${className}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-cyan-600" />
          <h3 className="text-lg font-semibold text-cyan-900">{title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-cyan-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-cyan-600" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4">
          {isLoading && (
            <div className="text-cyan-700 text-sm">Loading estate plan data...</div>
          )}

          {error && (
            <div className="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-2">
              Error loading estate plan data: {error}
            </div>
          )}

          {!isLoading && !error && !hasData && (
            <div className="text-cyan-700 text-sm italic">{emptyMessage}</div>
          )}

          {!isLoading && !error && hasData && (
            <div className="bg-white rounded-lg p-4 border border-cyan-100">
              <div className="mb-2 text-xs text-cyan-700 uppercase tracking-wide">
                From Estate Plan (Read-Only)
              </div>
              {renderData(plannerData)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * PlannerDataField - Simple key-value display for planner data fields
 */
export function PlannerDataField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string | number | null | undefined;
  className?: string;
}) {
  if (!value) return null;

  return (
    <div className={`mb-3 ${className}`}>
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="text-base text-gray-900 mt-1">{value}</div>
    </div>
  );
}

/**
 * PlannerDataList - Display array of items from planner data
 */
export function PlannerDataList({
  items,
  renderItem,
  emptyMessage = 'No items',
  className = '',
}: {
  items: unknown[];
  renderItem: (item: unknown, index: number) => React.ReactNode;
  emptyMessage?: string;
  className?: string;
}) {
  if (!items || items.length === 0) {
    return <div className={`text-gray-500 text-sm italic ${className}`}>{emptyMessage}</div>;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {items.map((item, index) => (
        <div key={index} className="border-b border-gray-100 pb-2 last:border-b-0">
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  );
}
